import { BlobReader, ZipReader } from "@zip.js/zip.js"
import { CreateProjectRequestDTO } from "@/api/dto/projects"
import { PermissionsDialog } from "@/components/Dialogs/PermissionsDialog"
import {
	SelectFolderDialog,
	SelectFolderDialogProps,
} from "@/components/Dialogs/SelectFolderDialog"
import {
	PhotoRecord,
	ProjectResetOption,
	ProjectInfoRecord,
	StyleInfoRecord,
	defaultCullingSettings,
} from "@/lib/catalog/types"
import { StyleRecord, clearEditedPreviews } from "@/storage/indexeddb"
import { getFiles, hasPermission } from "@/storage/file-system"
import { pushDialog, replaceDialog, resetDialogs } from "../dialogs-state"
import {
	allHiddenPhotosAtom,
	getRandomPhotosForStylePreview,
	photosAtom,
	searchInputAtom,
	searchResultPhotoIdsAtom,
} from "../photo/photos-state"
import { cleanupPreviews, previewsAtom } from "../previews/previews-state"
import { setImageInfoRecords } from "../processing/image-info"
import { previewQueue, processingQueue } from "../processing/processing-queue"
import { store } from "../store"
import {
	currentStyleAtom,
	isLocalStyle,
	RemoteStyle,
	SelectStyleOptions,
	showStylePreviewDialog,
} from "../style/style-state"
import { createProject, type NewStyleSetupOptions } from "./create-project"
import { ProjectItem, ProjectService } from "./projects-service"
import {
	createProjectAtom,
	displayGroupAtom,
	isCalibrationAtom,
	projectAtom,
	projectItemToProjectInfo,
	projectsAtom,
	projectSearchInputAtom,
	setIsCulling,
	syncProject,
	updateCurrentProject,
} from "./projects-state"
import toast from "react-hot-toast"
import { SupportedFormatsDialog } from "../../components/Dialogs/SupportedFormatsDialog"
import { Sentry } from "@/telemetry/sentry"
import { chooseAndValidateDirectory } from "@/storage/open-directory"
import { ConfirmDialog } from "@/components/Dialogs/ConfirmDialog"
import { allSupportedExtensions } from "@/io/formats/supported-extensions"
import { LoadingDialog } from "@/components/Dialogs/LoadingDialog"
import { getEditor } from "@/utils/useEditor"
import { clearPhotoLoadingState } from "../editor/photo-loading"
import {
	SelectEditModeDialog,
	SelectProjectModeDialog,
} from "@/components/Dialogs/SelectModeDialog"
import { SwitchToChromeDialog } from "@/components/Dialogs/SwitchToChromeDialog"
import { cancelOpenProject, setUpProject } from "./setup-project"
import { hidePhotos, unhidePhotos } from "../photo/store-photos"
import { handleProjectError } from "./handle-project-error"
import { showCullingHiddenPhotosTooltip } from "../app-state"
import { isProjectLocked } from "./lock-project"
import {
	stateAtom,
	defaultEditingExpandedPanels,
	defaultCullingExpandedPanels,
	PhotoId,
} from "../undoable-state"
import { queryClient } from "@/api/constants"
import { PreviewLoader } from "@/lib/metadata/metadata-loader"
import { cullingImporter } from "../importing/culling-importing"
import { updateKeepersAndRejects } from "../importing/culling-state"
import { adjustmentsHistoryAtom, baseHistoryAtom } from "../store-history"
import { restoreCustomPhotoOrdering } from "../photo/photos-custom-order"
import { metadataImporter } from "../importing/metadata-importing"
import { previewImporter } from "../importing/preview-importing"
import { editClusteringImporter } from "../importing/edit-clustering-importing"
import { cleanupPendingStyleApplication } from "../style/apply-style"
import { AI } from "@/lib/ai/ai"
import { Culler } from "@/lib/culling/culler"
import { AuxiliaryAIRawLoader, RawLoader } from "@/lib/raw/raw-loader"
import { resetTaskPerformanceMeasurements } from "../debug/task-performance-state"
import { highlightsClippingAtom, shadowsClippingAtom } from "../settings-state"
import { getHandleFromPath } from "@/utils/file"
import { autoSelectFirstPhotoIfNeeded, resetAllFilters } from "../photo/photos-view-state"
import { projectClosingAtom } from "../ui-views-state"
import { AxiosError } from "axios"
import { uuid } from "@/utils/uuid"
import { AbortError } from "@/utils/error"
import { AllStylesDialog } from "@/components/Dialogs/StylesListDialog"
import { DialogProps } from "@/components/ui/Dialog"
import { updateAIStyle } from "../style/update-style"
import { awaitMaybePromise } from "@/utils/promise"
import { CullingSettingsDialog } from "@/components/Dialogs/CullingSettingsDialog"
import { CatalogManager } from "@/lib/catalog"
import { removeProjectCatalogArtifacts } from "@/lib/catalog/catalog-file-operations"
import { routes } from "@/utils/routes"
import { StyleResponseDTO } from "@/api/dto/styles"
import { StyleService } from "../style/style-service"
import { closeSession } from "@/routing/project-session"
import { clearProjectModeTiming, markProjectModeEntered } from "./project-session-timing"
import { popActiveLoadingDialogs } from "@/store/loading-dialog-utils"
import { isNetworkConnectivityError, showActiveNetworkErrorToast } from "@/api/network-behavior"
import { ensureProjectCreationAllowed } from "./project-creation-guard"
import { browserCapabilitiesAtom } from "../app/browser-capabilities"
import { SelectFolderErrorDialog } from "@/components/Dialogs/SelectFolderErrorDialog"
import {
	FOLDER_SELECTION_GUIDANCE_EXPERIMENT,
	getExperimentVariant,
} from "@/utils/measurement/experiment"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer dialog/toast i18n, to be refactored to UI-layer translation
import i18n from "@/i18n"

export async function closeProject(
	saveChanges = true,
	options?: {
		skipNavigateHome?: boolean
		skipResetDialogs?: boolean
	},
) {
	// Prevent re-entry if already closing
	if (store.get(projectClosingAtom)) {
		console.warn("closeProject called while already closing")
		return
	}

	store.set(projectClosingAtom, true)
	const closingProjectId = store.get(projectAtom)?.id
	// Fast-close when network is offline.
	// This avoids waiting on in-flight tasks that may only fail after long timeouts.
	const isOffline = typeof navigator !== "undefined" && navigator.onLine === false

	const errorHandler = (error: any, step: string) => {
		console.error(`Error during ${step}`, error)
		Sentry.captureException(error, { tags: { step } })
		toast.error(i18n.t("store:project.errorClosingProject"), {
			duration: 3000,
		})
		return
	}

	try {
		replaceDialog(LoadingDialog, {
			title: saveChanges
				? i18n.t("store:project.savingProject")
				: i18n.t("store:project.closingProject"),
		})

		cleanupPendingStyleApplication()

		if (!isOffline) {
			// Normal close: wait for in-flight tasks to settle gracefully.
			await processingQueue.pauseQueue().catch((e) => errorHandler(e, "pauseProcessingQueue"))
			await previewQueue.pauseQueue().catch((e) => errorHandler(e, "pausePreviewQueue"))
		}

		await PreviewLoader.clearQueue().catch((e) => errorHandler(e, "clearPreviewLoaderQueue"))

		//Clear tasks after catalog is closed so we can continue with pending tasks when reopening project
		processingQueue.clearTasks()
		previewQueue.clearTasks()

		// Stop the metadata producer before draining culling and preview consumers,
		// then release the worker resources owned by each importer.

		if (isOffline) {
			// Fast offline close: don't wait for in-flight tasks that may be blocked on network/model timeouts.
			metadataImporter.cancel()
			cullingImporter.cancel()
			previewImporter.cancel()
			editClusteringImporter.cancel()
		} else {
			try {
				await metadataImporter.pauseAndCancel()
				await cullingImporter.pauseAndCancel({ cleanupModels: false })
				await previewImporter.pauseAndCancel()
				await editClusteringImporter.pauseAndCancel({ cleanupModels: true })
			} catch (e) {
				errorHandler(e, "pauseAndCancelImporters")
			}
		}

		await awaitMaybePromise(CatalogManager.unlockProject()).catch((e) =>
			errorHandler(e, "unlockProject"),
		)

		if (saveChanges) {
			try {
				await persistProjectUIState()
				await CatalogManager.flushDatabaseChanges()
			} catch (error) {
				errorHandler(error, "flushChanges")
			}
		}
		await awaitMaybePromise(CatalogManager.close()).catch((e) =>
			errorHandler(e, "closeCatalog"),
		)

		// Cleanup all resources
		try {
			await RawLoader.cleanup()
			await AuxiliaryAIRawLoader.cleanup()
			await AI.cleanupModels()
			await AI.cleanupEditor()
			const editor = getEditor()
			editor.clearViewport()
			editor.renderer.cleanup()
		} catch (e) {
			errorHandler(e, "cleanupResources")
		}

		cleanupPreviews()

		resetTaskPerformanceMeasurements()

		if (!options?.skipResetDialogs) {
			resetDialogs()
		}

		store.set(adjustmentsHistoryAtom, [])
		store.set(projectAtom, null)

		// Reset project filters and search input when navigating home
		store.set(projectSearchInputAtom, "")

		if (!options?.skipNavigateHome) {
			await navigateHomeIfStillOnProject().catch((e) => errorHandler(e, "navigateHome"))
		}
	} catch (error) {
		errorHandler(error, "closeProject")
	} finally {
		clearProjectModeTiming(closingProjectId)
		store.set(projectClosingAtom, false)
	}
}

const persistProjectUIState = async () => {
	const appState = store.get(stateAtom)
	const project = store.get(projectAtom)

	if (!project) {
		return
	}
	await CatalogManager.updateProjectInfo(project?.id, {
		photosViewState: appState.photosView,
		currentPhotoId: appState.currentPhotoId,
	})
}

export function blockSampleProjectExportIfNeeded(): boolean {
	const capabilities = store.get(browserCapabilitiesAtom)
	if (capabilities.supportsSampleOnly) {
		replaceDialog(SwitchToChromeDialog)
		return true
	}
	return false
}

export const chooseProjectMode = (props: {
	handle: FileSystemDirectoryHandle
	disableClose: boolean
	styleDTO?: StyleResponseDTO
	existingProjectInfo?: ProjectInfoRecord
	existingProject?: ProjectItem
	existingProjectId?: string
}) => {
	function closeConfirm(): Promise<boolean> {
		return new Promise((resolve) => {
			pushDialog(ConfirmDialog, {
				trackingViewName: "cancel_project_setup_confirmation",
				title: i18n.t("store:project.cancelSetupTitle"),
				message: i18n.t("store:project.cancelSetupMessage"),
				secondary: {
					title: i18n.t("store:project.cancelSetupSecondary"),
					handler: () => {
						const hasOpenProject = !!store.get(projectAtom)
						if (hasOpenProject) {
							void closeSession({ reason: "cancel_project_setup" })
						} else {
							resetDialogs()
							cancelOpenProject("cancel_project_setup")
						}
						resolve(true)
					},
				},
				primary: {
					title: i18n.t("store:project.cancelSetupPrimary"),
					handler: () => resolve(false),
				},
			})
		})
	}

	if (props.styleDTO) {
		void startEditing({ ...props, closeConfirm })

		return
	}
	const trackingProjectId =
		props.existingProjectId ?? props.existingProject?.id ?? props.existingProjectInfo?.id
	const trackingTotalPhotos = props.existingProject?.photoCount

	replaceDialog(SelectProjectModeDialog, {
		disableClose: props.disableClose,
		trackingProjectId,
		trackingTotalPhotos,
		onSelect: (mode) => {
			switch (mode) {
				case "smart_cull":
					void startCulling({
						...props,
						closeConfirm,
					})
					break
				case "style":
					void startEditing({ ...props, closeConfirm })
					break
				case "manual_edit":
					void startManualEditing(props)
					break
			}
		},
		closeConfirm,
		trackingViewName: "select_project_mode",
	})
}

export const startCulling = async (
	props: Partial<DialogProps> & {
		handle: FileSystemDirectoryHandle
		existingProjectInfo?: ProjectInfoRecord
		existingProject?: ProjectItem
		existingProjectId?: string
	},
) => {
	try {
		await updateCurrentProject({ cullingSettings: defaultCullingSettings })
		const trackingProjectId =
			props.existingProjectId ?? props.existingProject?.id ?? props.existingProjectInfo?.id
		const trackingTotalPhotos = props.existingProject?.photoCount

		await new Promise((resolve) => {
			pushDialog(CullingSettingsDialog, {
				...props,
				trackingProjectId,
				trackingTotalPhotos,
				onConfirm: () => resolve(undefined),
			})
		})

		if (!props.existingProject) {
			pushDialog(LoadingDialog, { title: i18n.t("store:project.creatingProject") })
		} else {
			pushDialog(LoadingDialog, { title: i18n.t("store:project.loadingProject") })
		}

		const projectId =
			props.existingProjectId ?? props.existingProject?.id ?? props.existingProjectInfo?.id
		const updatedProject = store.get(projectAtom) ?? props.existingProject //Culling settings dialog may have updated the project
		await resetProjectState("culling", projectId)
		await setUpProject({
			handle: props.handle,
			isCulling: true,
			existingProject: updatedProject,
			existingProjectInfo: props.existingProjectInfo,
		})
	} catch (error) {
		popActiveLoadingDialogs()
		if (isNetworkConnectivityError(error)) {
			showActiveNetworkErrorToast()
			return
		}

		void handleProjectError(error, { source: "start_culling" })
	}
}

export const switchToCulling = async (project: ProjectItem) => {
	if (!project.handle) {
		return
	}
	try {
		replaceDialog(LoadingDialog, {
			title: i18n.t("store:project.switchingToCulling"),
		})

		// Tear down the edit-mode AI style pipeline before entering culling.
		// Without this, editClusteringImporter keeps producing cluster results and
		// checkAndQueuePendingPhotos keeps calling queueApplyStyle, causing AI edits
		// continue to apply in culling mode.
		cleanupPendingStyleApplication()
		await editClusteringImporter.pauseAndCancel({ cleanupModels: false })

		await processingQueue.pauseQueue()

		processingQueue.clearTasks()
		void AI.cleanupModels()

		await resetProjectState("culling", project.id)
		await setUpProject({
			handle: project.handle,
			isCulling: true,
			existingProject: project,
			useCurrentSessionUI: true,
		})

		const currentProject = store.get(projectAtom)
		if (!currentProject || currentProject.id !== project.id || !currentProject.isCulling) {
			// Setup failed/canceled and handled elsewhere (toast/dialog/navigation). Avoid leaving loading UI.
			popActiveLoadingDialogs()
			return
		}

		const hiddenPhotos = store.get(allHiddenPhotosAtom)
		if (hiddenPhotos.length > 0) {
			replaceDialog(ConfirmDialog, {
				trackingViewName: "restore_hidden_photos",
				title: i18n.t("store:project.restoreHiddenTitle"),
				message: i18n.t("store:project.restoreHiddenMessage", {
					count: hiddenPhotos.length,
				}),
				primary: {
					title: i18n.t("store:project.restoreHiddenPrimary"),
					handler: async () => {
						await unhidePhotos(hiddenPhotos.map((photo) => photo.id))
					},
				},
				secondary: {
					title: i18n.t("common:action.cancel"),
				},
			})
		}
	} catch (error) {
		popActiveLoadingDialogs()
		if (isNetworkConnectivityError(error)) {
			showActiveNetworkErrorToast()
			return
		}
		void handleProjectError(error, { source: "switch_to_culling" })
	}
}

export const startEditing = async (
	props: Partial<DialogProps> & {
		handle: FileSystemDirectoryHandle
		style?: StyleRecord
		styleDTO?: StyleResponseDTO
		existingProjectInfo?: ProjectInfoRecord
		existingProject?: ProjectItem
		existingProjectId?: string
		clearExistingEdits?: boolean
	},
) => {
	const { clearExistingEdits, ...styleSelectionProps } = props
	let isSetupInFlight = false
	const runProjectSetup = async (selectedStyleOptions?: SelectStyleOptions) => {
		if (isSetupInFlight) {
			return
		}
		isSetupInFlight = true
		try {
			if (!props.existingProject) {
				pushDialog(LoadingDialog, {
					title: i18n.t("store:project.creatingProject"),
				})
			} else {
				pushDialog(LoadingDialog, {
					title: i18n.t("store:project.loadingProject"),
				})
			}

			const projectId =
				props.existingProjectId ??
				props.existingProject?.id ??
				props.existingProjectInfo?.id

			if (clearExistingEdits && props.existingProject) {
				const projectInfo =
					props.existingProjectInfo ?? projectItemToProjectInfo(props.existingProject)
				await clearProject(projectInfo, ["edits"])
			}

			await resetProjectState("editing", projectId)
			if (selectedStyleOptions) {
				await storePublishedStyleSelection(selectedStyleOptions)
			}

			await setUpProject({
				handle: props.handle,
				selectedStyle: selectedStyleOptions?.style,
				aiStyleTriggerSource: "apply_ai_style",
				needsSetup: selectedStyleOptions?.needsSetup ?? (props.style ? false : undefined),
				isExpressSetup: selectedStyleOptions?.isExpressSetup,
				existingProject: props.existingProject,
				existingProjectInfo: props.existingProjectInfo,
				existingProjectStyle: props.style,
			})
		} catch (error) {
			popActiveLoadingDialogs()
			if (isNetworkConnectivityError(error)) {
				showActiveNetworkErrorToast()
				return
			}
			void handleProjectError(error, { source: "start_editing" })
		} finally {
			isSetupInFlight = false
		}
	}

	if (props.style) {
		await runProjectSetup()
		return
	}

	const selectedStyleOptions = await chooseStyle({
		...styleSelectionProps,
		isSwitching: false,
		canGoBack: true,
		onReselect: (options) => {
			void runProjectSetup(options)
		},
	})

	await runProjectSetup(selectedStyleOptions)
}

export const startManualEditing = async (props: {
	handle: FileSystemDirectoryHandle
	existingProjectInfo?: ProjectInfoRecord
	existingProject?: ProjectItem
	existingProjectId?: string
}) => {
	try {
		if (!props.existingProject) {
			pushDialog(LoadingDialog, { title: i18n.t("store:project.creatingProject") })
		} else {
			pushDialog(LoadingDialog, { title: i18n.t("store:project.loadingProject") })
		}

		const projectId =
			props.existingProjectId ?? props.existingProject?.id ?? props.existingProjectInfo?.id

		await resetProjectState("editing", projectId)

		await setUpProject({
			handle: props.handle,
			selectedStyle: "manual",
			needsSetup: false,
			isExpressSetup: false,
			isCulling: false,
			existingProject: props.existingProject,
			existingProjectInfo: props.existingProjectInfo,
		})
	} catch (error) {
		popActiveLoadingDialogs()
		if (isNetworkConnectivityError(error)) {
			showActiveNetworkErrorToast()
			return
		}
		void handleProjectError(error, { source: "start_manual_editing" })
	}
}

export const finishCulling = async (unselectedPhotos: PhotoRecord[]) => {
	const project = store.get(projectAtom)

	if (!project || !project.handle) {
		return
	}
	const projectHandle = project.handle

	let styleOptions: SelectStyleOptions | undefined = undefined
	let styleToUpdate: StyleInfoRecord | undefined = undefined
	let existingStyle: StyleInfoRecord | undefined = undefined
	if (project.styleId) {
		const styleInfo = await CatalogManager.fetchStyleInfo()
		if (styleInfo) {
			existingStyle = styleInfo
			styleOptions = { style: styleInfo, needsSetup: false }
			styleToUpdate = unselectedPhotos.some((photo) => photo.kind === "reference")
				? styleInfo
				: undefined
		}
	} else if (project.needsSetup) {
		styleOptions = {
			style: "new",
			needsSetup: project.needsSetup,
		}
	} else if (project.isManual) {
		styleOptions = { style: "manual", needsSetup: false }
	}

	const previewAvoidingIds = unselectedPhotos.map((p) => p.id)

	let isSetupInFlight = false
	const runFinishCullingSetup = async (selectedStyleOptions: SelectStyleOptions) => {
		if (isSetupInFlight) {
			return
		}
		isSetupInFlight = true
		try {
			// Once a non-manual editing path is selected, original-preview work is no
			// longer needed. Drain active renders and release the AI editor lock before
			// starting the AI editing phase.
			if (selectedStyleOptions.style !== "manual") {
				await previewImporter.pauseAndCancel()
			}

			// Metadata can enqueue culling work. Stop that producer first, then drain
			// both Culler consumers before terminating the worker so no request is cut
			// off mid-call. Importers are restarted by setUpProject below if needed.
			await metadataImporter.pauseAndWaitForInFlightTasksToComplete()
			await Promise.all([
				cullingImporter.pauseAndCancel({ cleanupModels: false }),
				editClusteringImporter.pauseAndCancel({ cleanupModels: false }),
			])
			await Culler.reset()

			await resetProjectState("editing", project.id)
			replaceDialog(LoadingDialog, {
				title: i18n.t("store:project.preparingPhotos"),
			})
			await hidePhotos(
				unselectedPhotos.map((photo) => photo.id),
				false,
			)

			//Updates style if reference photos were unselected and hidden
			if (styleToUpdate) {
				void updateAIStyle(styleToUpdate, "finish_culling")
			}

			resetAllFilters()

			resetDialogs()
			await storePublishedStyleSelection(selectedStyleOptions)

			await setUpProject({
				handle: projectHandle,
				aiStyleTriggerSource: "finish_culling",
				selectedStyle: selectedStyleOptions.style,
				needsSetup: selectedStyleOptions.needsSetup,
				isExpressSetup: selectedStyleOptions.isExpressSetup,
				isCulling: false,
				existingProject: project,
				existingProjectStyle: existingStyle,
				useCurrentSessionUI: true,
			})

			if (unselectedPhotos.length > 0) {
				showCullingHiddenPhotosTooltip()
			}
		} catch (error) {
			popActiveLoadingDialogs()
			if (isNetworkConnectivityError(error)) {
				showActiveNetworkErrorToast()
				return
			}
			void handleProjectError(error, { source: "finish_culling" })
		} finally {
			isSetupInFlight = false
		}
	}

	if (!styleOptions) {
		styleOptions = await new Promise((resolve) => {
			pushDialog(SelectEditModeDialog, {
				onSelect: async (mode) => {
					switch (mode) {
						case "style":
							resolve(
								await chooseStyle({
									disableClose: false,
									isSwitching: false,
									canGoBack: true,
									previewAvoidingIds,
									onReselect: (reselectedStyleOptions) => {
										void runFinishCullingSetup(reselectedStyleOptions)
									},
								}),
							)
							break
						case "manual_edit":
							resolve({ style: "manual", needsSetup: false })
							break
					}
				},
				editOnly: true,
				trackingViewName: "select_edit_mode",
			})
		})
	}

	if (!styleOptions) {
		return
	}

	await runFinishCullingSetup(styleOptions)
}

export const setupProjectUIState = (
	mode: "editing" | "culling",
	project?: ProjectItem,
	projectInfo?: ProjectInfoRecord,
	options?: {
		preferredPhotosViewState?: "grid" | "focus" | "before/after"
		preferredCurrentPhotoId?: string
	},
) => {
	markProjectModeEntered(mode, project?.id ?? projectInfo?.id)

	const currentState = store.get(stateAtom)

	let photosViewState = options?.preferredPhotosViewState ?? projectInfo?.photosViewState
	if (!photosViewState) {
		if (project?.isManual || project?.needsSetup) {
			photosViewState = "focus"
		} else {
			photosViewState = "grid"
		}
	}
	store.set(stateAtom, {
		...currentState,
		photosView: photosViewState,
		currentPhotoId: options?.preferredCurrentPhotoId ?? projectInfo?.currentPhotoId ?? null,
		showingLeftPanel: true,
		showingRightPanel: true,
		activeEditingToolPanel: mode === "editing" ? "edit" : null,
		leftExpandedPanel: null,
		expandedPanels:
			mode === "editing" ? defaultEditingExpandedPanels : defaultCullingExpandedPanels,
	})

	// disable color clipping when switching to culling mode, but keep the settings
	if (mode === "culling") {
		getEditor().toggleColorClipping(false, false, false)
	} else {
		const highlightsClipping = store.get(highlightsClippingAtom)
		const shadowsClipping = store.get(shadowsClippingAtom)

		getEditor().toggleColorClipping(highlightsClipping, shadowsClipping, false)
	}

	autoSelectFirstPhotoIfNeeded()

	store.set(baseHistoryAtom, {
		index: 0,
		history: [store.get(stateAtom)],
		displayNames: ["Original state"],
	})
	restoreCustomPhotoOrdering()

	getEditor().clearViewport()
	clearPhotoLoadingState()

	// Clear search state
	store.set(searchInputAtom, "")
	store.set(searchResultPhotoIdsAtom, new Set())
}

export const resetProjectState = async (mode: "editing" | "culling", projectId?: string) => {
	store.set(isCalibrationAtom, false)
	// Await local/catalog mode write before continuing setup to keep transition order deterministic.
	await setIsCulling(mode === "culling", projectId)
}

export const chooseStyle = (
	props: Partial<DialogProps> & {
		isSwitching: boolean
		styleDTO?: StyleResponseDTO
		previewAvoidingIds?: PhotoId[]
		onReselect?: (options: SelectStyleOptions) => void
	},
): Promise<SelectStyleOptions> => {
	return new Promise((resolve) => {
		let hasResolved = false
		const showStyleSelectionDialog = () => {
			pushDialog(AllStylesDialog, {
				...props,
				onSelect: handleStyleSelected,
				trackingViewName: props.isSwitching ? "switch_style_dialog" : "select_style_dialog",
				previewAvoidingIds: props.previewAvoidingIds,
			})
		}

		const handleStyleSelected = (options: SelectStyleOptions) => {
			if (!hasResolved) {
				hasResolved = true
				resolve(options)
				return
			}
			props.onReselect?.(options)
		}

		if (props.styleDTO) {
			void (async () => {
				const style = props.styleDTO
				if (!style) {
					return
				}

				// Show loading dialog while fetching style references
				replaceDialog(LoadingDialog, {
					title: i18n.t("store:project.loadingAiStyle"),
				})
				try {
					const references = await StyleService.getReferencesForStyle(style.styleId)

					const remoteStyle = new RemoteStyle(style, references)
					const previewPhotos = getRandomPhotosForStylePreview(props.previewAvoidingIds)
					const projectStyleType =
						style.author.name === "Pixieset" ? "Pixieset AI Style" : "My AI Style"

					showStylePreviewDialog(
						{
							...props,
							trackingViewName: props.trackingViewName ?? "ai_style_page",
							canGoBack: false,
						},
						remoteStyle,
						projectStyleType,
						previewPhotos,
						(style) => {
							handleStyleSelected({ style, needsSetup: false })
						},
					)
				} catch (error) {
					console.error("Failed to load AI Style preview", error)
					popActiveLoadingDialogs()
					if (isNetworkConnectivityError(error)) {
						showActiveNetworkErrorToast()
					} else {
						toast.error(i18n.t("store:project.errorLoadAiStyle"))
					}
					showStyleSelectionDialog()
				}
			})()
		} else {
			showStyleSelectionDialog()
		}
	})
}

const storePublishedStyleSelection = async (options: SelectStyleOptions) => {
	const style = options.style
	if (
		!options.publishedReferences ||
		style === "new" ||
		style === "manual" ||
		!isLocalStyle(style)
	) {
		return
	}

	await CatalogManager.storeStyleInfo(style)
	await CatalogManager.replaceStyleReferences(options.publishedReferences)
}

export function showSelectFolderDialog(
	props?: Omit<SelectFolderDialogProps, "showFolderSelectionGuidance">,
) {
	if (!ensureProjectCreationAllowed()) {
		return
	}

	void getExperimentVariant(FOLDER_SELECTION_GUIDANCE_EXPERIMENT).then((variant) => {
		replaceDialog(SelectFolderDialog, {
			...props,
			showFolderSelectionGuidance: variant === "treatment",
		})
	})
}

export function showSupportedFormatsDialog() {
	pushDialog(SupportedFormatsDialog)
}

export function showSelectFolderErrorDialog(
	props?: Omit<SelectFolderDialogProps, "showFolderSelectionGuidance">,
) {
	replaceDialog(SelectFolderErrorDialog, {
		onConfirm: () => {
			showSelectFolderDialog(props)
		},
	})
}

/** Used by SelectFolderDialog to open folder for project creation flows */
export async function selectFolder(
	style: StyleRecord | null = null,
	styleDTO?: StyleResponseDTO,
	newStyleSetup?: NewStyleSetupOptions,
) {
	if (!ensureProjectCreationAllowed()) {
		return
	}

	setTimeout(() => replaceDialog(PermissionsDialog), 1000)

	const result = await chooseAndValidateDirectory("import_photo", undefined, [
		"no_handle",
		"no_directory",
	])
	resetDialogs()

	if (result instanceof FileSystemDirectoryHandle) {
		createProject(result, style, styleDTO, newStyleSetup)
	} else if (result === "no_handle" || result === "no_directory") {
		showSelectFolderErrorDialog({
			selectedStyle: style ?? undefined,
			selectedStyleDTO: styleDTO,
			newStyleSetup,
		})
	}
}

export function importZipFiles({ style }: { style?: StyleRecord }) {
	if (!ensureProjectCreationAllowed()) {
		return
	}

	const input = document.createElement("input")
	input.type = "file"

	input.onchange = async (e) => {
		// @ts-expect-error `files` is not defined on base type EventTarget
		const files = e.target?.files ?? []
		for (const file of files) {
			await importZipFile({ file, style })
		}
	}

	input.click()
}

export async function importZipFile({ file, style }: { file: File; style?: StyleRecord }) {
	if (!ensureProjectCreationAllowed()) {
		return
	}

	replaceDialog(LoadingDialog, { title: `Importing ${file.name}...` }) // i18n-ignore
	try {
		const opfsRoot = await navigator.storage.getDirectory()
		const zipReader = new ZipReader(new BlobReader(file))
		const entries = await zipReader.getEntries()
		const zipRoot = entries[0]
		if (!zipRoot?.directory) {
			return
		}

		for (const entry of entries) {
			if (entry.directory) {
				continue
			}
			if (!entry.getData) {
				continue
			}
			const fileHandle = (await getHandleFromPath({
				create: true,
				fullPath: entry.filename,
				root: opfsRoot,
			})) as FileSystemFileHandle
			const writable = await fileHandle.createWritable()
			await entry.getData(writable)
		}
		await zipReader.close()
		const directoryHandle = (await getHandleFromPath({
			root: opfsRoot,
			fullPath: zipRoot.filename,
			create: false,
		})) as FileSystemDirectoryHandle
		createProject(directoryHandle, style)
	} finally {
		resetDialogs()
	}
}

async function navigateHomeIfStillOnProject() {
	const { router } = await import("@/router")
	await routes.root.navigate(router)
}

async function fetchExistingProject(options: {
	id?: string
	folder?: FileSystemDirectoryHandle
}): Promise<ProjectItem | undefined> {
	const { data: projects } = store.get(projectsAtom)

	// Check by ID first (faster)
	for (const project of projects) {
		if (project.id === options.id) {
			return project
		}
	}

	// Otherwise, check by folder entry (slower)
	for (const project of projects) {
		if (
			options.folder &&
			project.handle &&
			(await options.folder.isSameEntry(project.handle))
		) {
			return project
		}
	}

	return undefined
}

export async function fetchOrCreateProject(
	folder: FileSystemDirectoryHandle,
	projectInfo?: ProjectInfoRecord,
): Promise<{ project: ProjectItem; projectInfo: ProjectInfoRecord }> {
	const project = await fetchExistingProject({
		id: projectInfo?.id,
		folder,
	})

	if (project) {
		// It is possible that existing project item does not have a handle set (never opened in this browser),
		// so we need to ensure it has one
		const projectWithHandle = { ...project, handle: folder }
		return {
			project: projectWithHandle,
			projectInfo: projectInfo ?? projectItemToProjectInfo(project),
		}
	}

	const createProjectViaApi = store.get(createProjectAtom).mutateAsync as (
		project: CreateProjectRequestDTO,
	) => Promise<ProjectItem>
	const createDTO: CreateProjectRequestDTO = {
		projectId: projectInfo?.id,
		name: projectInfo?.name ?? folder.name ?? "Default Project Name",
		handle: folder,
	}
	const newProject = await createProjectViaApi(createDTO)

	const newProjectInfo = {
		...(projectInfo ?? projectItemToProjectInfo(newProject)),
		id: newProject.id,
	}
	await CatalogManager.storeProjectInfo({
		record: newProjectInfo,
		oldIdToBeReplaced: projectInfo?.id,
	})

	return { project: newProject, projectInfo: newProjectInfo }
}

export const deleteProject = async (project: ProjectItem, cleanupFiles: boolean) => {
	try {
		await ProjectService.deleteProject(project)
		await queryClient.invalidateQueries({ queryKey: ["projects"] })

		if (cleanupFiles) {
			if (!project.handle) {
				toast.error(i18n.t("store:project.errorDeleteNoFolder"), {
					duration: 5000,
				})
				return
			}

			if (!(await hasPermission(project.handle, "readwrite", true))) {
				toast.error(i18n.t("store:project.errorDeleteNoCatalogPermission"), {
					duration: 5000,
				})
				return
			}

			if (await isProjectLocked(project.handle)) {
				toast.error(i18n.t("store:project.errorDeleteProjectLocked"), {
					duration: 5000,
				})
				return
			}

			try {
				await removeProjectCatalogArtifacts(project.handle, { ignoreNotFound: true })
			} catch (error) {
				console.error("[Error] cleaning up deleted project catalog artifacts", error)
				Sentry.captureException(error)
			}
		}
		toast.success(i18n.t("store:project.deleteProjectSuccess", { name: project.name }))
	} catch (error) {
		toast.error(i18n.t("store:project.deleteProjectError", { name: project.name }))
		console.error("[Error] deleting project", error)
		Sentry.captureException(error)
	}
}

export type ResetProjectOption = "edits" | "ratings"

export const clearProject = async (
	projectInfo: ProjectInfoRecord,
	options: ProjectResetOption[],
) => {
	if (options.length === 0) {
		return
	}

	await clearProjectInfo(projectInfo, options)

	if (options.includes("edits")) {
		await clearProjectEdits(projectInfo)
	}

	await CatalogManager.bulkResetAllPhotoRecords(options)
	const photos = await CatalogManager.fetchAllPhotoRecords()
	store.set(photosAtom, photos)

	if (options.includes("ratings")) {
		await resetSmartCullingTagsToDefaultSettings()
	}
}

async function resetSmartCullingTagsToDefaultSettings() {
	const project = store.get(projectAtom)
	if (!project?.isCulling) {
		return
	}

	store.set(projectAtom, {
		...project,
		cullingSettings: defaultCullingSettings,
		suggestedKeeperCount: defaultCullingSettings.suggestedKeeperCount,
	})

	await updateKeepersAndRejects(defaultCullingSettings.suggestedKeeperCount)
}

const projectInUseConfirmReset = (): Promise<boolean> => {
	return new Promise((resolve) => {
		replaceDialog(ConfirmDialog, {
			trackingViewName: "project_in_use",
			title: i18n.t("store:project.projectInUseTitle"),
			message: i18n.t("store:project.projectInUseMessage"),
			primary: {
				title: i18n.t("store:project.projectInUsePrimary"),
				isDestructive: true,
				handler: () => {
					resetDialogs()
					resolve(true)
				},
			},
			secondary: {
				title: i18n.t("common:action.cancel"),
				handler: () => {
					resetDialogs()
					resolve(false)
				},
			},
			canClose: false,
		})
	})
}
async function clearProjectEdits(projectInfo: ProjectInfoRecord) {
	await processingQueue.pauseQueue()
	processingQueue.clearTasks()

	//Image Info
	const updatedImageInfo = await CatalogManager.clearImageInfo()
	setImageInfoRecords(updatedImageInfo)

	//Edits
	await CatalogManager.clearAllPhotosEditsAndStyle()

	//Previews
	await clearEditedPreviews(projectInfo.id)
	store.set(previewsAtom, {})

	//Style
	const projectStyle = await CatalogManager.fetchStyleInfo()
	if (projectStyle) {
		await CatalogManager.deleteStyleInfo(projectStyle.styleId)
		await CatalogManager.clearStyleReferences()
	} else {
		store.set(isCalibrationAtom, false)
		store.set(displayGroupAtom, "all")
	}
	store.set(currentStyleAtom, null)

	await CatalogManager.flushDatabaseChanges()
}

async function clearProjectInfo(projectInfo: ProjectInfoRecord, options: ProjectResetOption[]) {
	let infoToClear: Partial<ProjectInfoRecord> = {}
	let itemToClear: Partial<ProjectItem> = {}

	if (options.includes("ratings")) {
		infoToClear = {
			...infoToClear,
			cullingSettings: defaultCullingSettings,
			suggestedKeeperCount: defaultCullingSettings.suggestedKeeperCount,
		}
	}

	if (options.includes("edits")) {
		infoToClear = {
			...infoToClear,
			styleId: null,
			isManual: false,
			needsSetup: undefined,
			isExpressSetup: undefined,
		}
		itemToClear = {
			...itemToClear,
			styleId: null,
			isManual: false,
			needsSetup: undefined,
			isExpressSetup: undefined,
			previewThumbnailUrls: [], //Only exists on ProjectItem
		}
	}

	if (Object.keys(infoToClear).length === 0 && Object.keys(itemToClear).length === 0) {
		return
	}

	//Sync Project and Project Info to BE and make sure we have permissions to clear
	const currentProject = store.get(projectAtom)
	const isSampleProject = currentProject?.id === projectInfo.id && currentProject.isSampleProject

	let useNewProjectId = false
	if (isSampleProject) {
		// Sample projects are read-only on the backend; skip the sync.
	} else {
		try {
			await syncProject(projectInfo.id, itemToClear)
		} catch (error) {
			if (error instanceof AxiosError && error.response?.status === 403) {
				// Project id is in use by another account (e.g. using same catalog with different accounts), we can recreate a project with a different id
				const shouldReset = await projectInUseConfirmReset()
				if (shouldReset) {
					useNewProjectId = true
				} else {
					throw new AbortError("Project in use by another account")
				}
			} else if (error instanceof AxiosError && error.response?.status === 404) {
				// Project is not found (e.g. deleted but didnt remove catalog, using same catalog in staging/production), we can recreate a project with the same id
			} else {
				throw error
			}
		}
	}

	if (useNewProjectId) {
		const newId = uuid()
		const newInfo = { ...projectInfo, ...infoToClear, id: newId }
		await CatalogManager.storeProjectInfo({
			record: newInfo,
			oldIdToBeReplaced: projectInfo.id,
		})
	} else {
		await CatalogManager.updateProjectInfo(projectInfo.id, infoToClear)
	}
}

export async function getCompatibleFiles(directory: FileSystemDirectoryHandle) {
	const result = await getFiles(directory, null, {
		allowedExtensions: allSupportedExtensions,
	})

	const totalFilesCount = Object.keys(result.files).length
	if (totalFilesCount === 0) {
		toast(i18n.t("store:project.noCompatibleFiles"))
		return null
	}

	return result
}

export function isProjectSetupIncomplete(
	projectInfo: ProjectInfoRecord | ProjectItem | undefined | null,
) {
	if (!projectInfo) {
		return false
	}
	return (
		(projectInfo.styleId === null || projectInfo.styleId === undefined) && //Not editing with a style
		!projectInfo.needsSetup && //Not setting up a style
		!projectInfo.isCulling && //Not culling
		!projectInfo.isManual //Not manual editing
	)
}
